import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Award, Users, Briefcase, Shield } from 'lucide-react';
import { images } from '../data/mock';

const About = () => {
  const { t, direction } = useLanguage();

  const stats = [
    { icon: Award, value: '15+', label: t('aboutYears') },
    { icon: Briefcase, value: '500+', label: t('aboutCases') },
    { icon: Users, value: '300+', label: t('aboutClients') },
    { icon: Shield, value: '100%', label: t('language') === 'ar' ? 'نسبة النجاح' : 'Success Rate' }
  ];

  return (
    <section id="about" className="relative py-24 bg-white">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-100/50 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-100/50 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Section */}
          <div className="relative order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-200/40 to-amber-300/40 rounded-2xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border-2 border-amber-200 shadow-2xl">
                <img 
                  src={images.about} 
                  alt="Legal Library"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/60 to-transparent" />
              </div>
            </div>

            {/* Stats overlay */}
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 w-11/12">
              <div className="grid grid-cols-2 gap-4">
                {stats.slice(0, 2).map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div 
                      key={index}
                      className="bg-white border-2 border-amber-200 rounded-xl p-4 text-center shadow-xl"
                    >
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-2xl font-bold text-slate-900 mb-1">{stat.value}</div>
                      <div className="text-sm text-slate-600">{stat.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="space-y-8 order-1 lg:order-2">
            <div>
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-amber-100 border border-amber-300 rounded-full text-amber-700 text-sm font-medium">
                  {t('about')}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight whitespace-nowrap">
                {t('aboutTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('aboutDescription')}
              </p>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-2 gap-6 pt-8">
              {stats.slice(2).map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div 
                    key={index}
                    className="bg-amber-50 border border-amber-200 rounded-xl p-6 hover:border-amber-300 transition-all duration-300 group"
                  >
                    <div className="w-14 h-14 mb-4 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-all">
                      <Icon className="w-7 h-7 text-amber-600" />
                    </div>
                    <div className="text-3xl font-bold text-slate-900 mb-2">{stat.value}</div>
                    <div className="text-sm text-slate-600">{stat.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Why Choose Us Points */}
            <div className="space-y-4 pt-4">
              {[
                { title: t('why1Title'), desc: t('why1Desc') },
                { title: t('why2Title'), desc: t('why2Desc') }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="p-4 bg-amber-50 rounded-lg border border-amber-200 hover:border-amber-300 transition-all duration-300"
                >
                  <div 
                    className="flex items-start gap-4 justify-end"
                  >
                    <div className="text-right">
                      <h4 className="text-slate-900 font-semibold mb-1">{item.title}</h4>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                    <div className="w-2 h-2 mt-2 bg-amber-600 rounded-full flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
